import { Coins, CircleDollarSign, OctagonAlert, Users, FileWarning, CalendarDays, WifiOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ClientIndicators, DelegationInfo } from '@/api/visits';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface ClientIndicatorBadgesProps {
  indicators: ClientIndicators | undefined;
  /** Override visitFrequencyWeeks (per-item value takes precedence over client-level indicator) */
  visitFrequencyWeeks?: number;
}

export function ClientIndicatorBadges({ indicators, visitFrequencyWeeks }: ClientIndicatorBadgesProps) {
  // visitFrequencyWeeks can be shown even without other indicators
  const frequencyValue = visitFrequencyWeeks ?? indicators?.visitFrequencyWeeks ?? 1;

  if (!indicators) {
    // Still show frequency indicator if we have per-item value
    if (frequencyValue !== 1) {
      return (
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <FrequencyIndicator weeks={frequencyValue} />
        </div>
      );
    }
    return null;
  }

  return (
    <div className="flex items-center gap-0.5 flex-shrink-0">
      {/* Internet problem */}
      <InternetProblemIndicator hasIssue={indicators.hasInternetProblem} />

      {/* Payment status */}
      <PaymentIndicator count={indicators.unpaidCount} />

      {/* Delegations */}
      <DelegationIndicator delegations={indicators.delegations} />

      {/* Visit frequency (only if != 1) - use per-item value if available */}
      <FrequencyIndicator weeks={frequencyValue} />

      {/* Unsent documents */}
      <DocsIndicator overdue={indicators.docs.overdue} total={indicators.docs.total} />
    </div>
  );
}

function InternetProblemIndicator({ hasIssue }: { hasIssue: boolean }) {
  if (!hasIssue) return null;

  return (
    <div
      className="flex items-center justify-center w-5 h-5 rounded bg-red-100"
      title="Проблемы с интернетом"
    >
      <WifiOff className="w-3.5 h-3.5 text-red-600" />
    </div>
  );
}

function PaymentIndicator({ count }: { count: number }) {
  if (count === 0) return null;

  if (count >= 3) {
    return (
      <div className="flex items-center justify-center w-5 h-5 rounded bg-red-100" title="3+ неоплаченных счёта">
        <OctagonAlert className="w-3.5 h-3.5 text-red-600" />
      </div>
    );
  }

  if (count === 2) {
    return (
      <div className="flex items-center justify-center w-5 h-5 rounded bg-amber-100" title="2 неоплаченных счёта">
        <Coins className="w-3.5 h-3.5 text-amber-600" />
      </div>
    );
  }

  // count === 1
  return (
    <div className="flex items-center justify-center w-5 h-5 rounded bg-amber-50" title="1 неоплаченный счёт">
      <CircleDollarSign className="w-3.5 h-3.5 text-amber-500" />
    </div>
  );
}

function DelegationIndicator({ delegations }: { delegations: DelegationInfo[] }) {
  if (delegations.length === 0) return null;

  const firstDelegation = delegations[0];
  const hasMore = delegations.length > 1;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-0.5 h-5 px-1 rounded bg-blue-100 text-blue-700 text-[10px] font-medium"
          title="Делегирован"
        >
          <Users className="w-3 h-3" />
          <span>{firstDelegation.initials}</span>
          {hasMore && <span className="text-[9px]">+{delegations.length - 1}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[160px]">
        <DropdownMenuLabel className="text-xs">Делегирован:</DropdownMenuLabel>
        {delegations.map((d) => (
          <DropdownMenuItem key={d.toEmployeeId} className="text-xs">
            {d.fullName}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function FrequencyIndicator({ weeks }: { weeks: number }) {
  if (weeks === 1) return null;

  return (
    <div
      className="flex items-center gap-0.5 h-5 px-1 rounded bg-gray-100 text-gray-600 text-[10px] font-medium"
      title={`Раз в ${weeks} недель`}
    >
      <CalendarDays className="w-3 h-3" />
      <span>1/{weeks}</span>
    </div>
  );
}

function DocsIndicator({ overdue, total }: { overdue: number; total: number }) {
  if (total === 0) return null;

  const hasOverdue = overdue > 0;

  return (
    <div
      className={cn(
        'flex items-center gap-0.5 h-5 px-1 rounded text-[10px] font-medium',
        hasOverdue ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
      )}
      title={hasOverdue ? `${overdue} просроченных из ${total} несданных` : `${total} несданных документов`}
    >
      <FileWarning className="w-3 h-3" />
      <span>{hasOverdue ? `${overdue}/${total}` : total}</span>
    </div>
  );
}
